// websocketMiddleware.js
import { setWebSocketStatus } from "../../store/entities/web-sock-status-slice";
import { setLoginData } from "../../store/entities/login-slice";
import store from "../../store/configure-store";
import plinkoTransport from "../../services/plinko-transport/plinko-transport";
import { setServerMessage } from "../../store/entities/server-messages-slice";

const websocketMiddleware = () => {
  let socket = null;
  let reconnectAttempts = 0;
  let isStale = false;

  // === HEARTBEAT ===
  // BE očekuje "ping" (string) svakih ~60s
  const PING_EVERY_MS = 35_000; // sigurno ispod 60s

  let pingInterval = null;
  let heartbeatStarted = false;

  // === BALANCE ===
  let balanceInterval = null;
  let unsubscribePendingWatcher = null;
  let hadPending = false;

  // ================= HELPERS =================
  const stopHeartbeat = () => {
    if (pingInterval) clearInterval(pingInterval);
    pingInterval = null;
    heartbeatStarted = false;
  };

  const stopBalancePolling = () => {
    if (balanceInterval) clearInterval(balanceInterval);
    balanceInterval = null;
  };

  const unwatchPending = () => {
    if (unsubscribePendingWatcher) {
      unsubscribePendingWatcher();
      unsubscribePendingWatcher = null;
    }
  };

  const hasActivePending = () => {
    const pb = store.getState()?.pendingBets;
    if (!pb) return false;
    if (typeof pb.activeCount === "number") return pb.activeCount > 0;
    if (Array.isArray(pb.ids)) return pb.ids.length > 0;
    if (pb.byId) return Object.keys(pb.byId).length > 0;
    return false;
  };

  const shouldPauseBalance = () => {
    const st = store.getState();
    return (
      Boolean(st?.autoPlay) ||
      Boolean(st?.turboMode?.state) ||
      hasActivePending()
    );
  };

  const sendRaw = (raw) => {
    if (!socket || socket.readyState !== WebSocket.OPEN || isStale) return;
    socket.send(raw);
  };

  const send = (payload) => {
    if (!socket || socket.readyState !== WebSocket.OPEN || isStale) return;
    socket.send(JSON.stringify(payload));
  };

  // ================= HEARTBEAT =================
  const sendPing = () => {
    if (!socket || socket.readyState !== WebSocket.OPEN || isStale) return;

    //console.log("[WS] -> ping");
    sendRaw(JSON.stringify("ping"));
  };

  const startHeartbeat = () => {
    if (heartbeatStarted || isStale) return;

    heartbeatStarted = true;
    sendPing();

    pingInterval = setInterval(() => {
      sendPing();
    }, PING_EVERY_MS);
  };

  // ================= BALANCE =================
  const watchPendingAndAutoFetch = (sid) => {
    if (unsubscribePendingWatcher) unsubscribePendingWatcher();
    hadPending = hasActivePending();

    unsubscribePendingWatcher = store.subscribe(() => {
      const now = hasActivePending();
      if (hadPending && !now && !shouldPauseBalance()) {
        send(["get_user_balance", { sid }]);
      }
      hadPending = now;
    });
  };

  const startBalancePolling = (sid) => {
    if (!shouldPauseBalance()) {
      send(["get_user_balance", { sid }]);
    }

    stopBalancePolling();
    balanceInterval = setInterval(() => {
      if (!shouldPauseBalance()) {
        send(["get_user_balance", { sid }]);
      }
    }, 30_000);

    watchPendingAndAutoFetch(sid);
  };

  // ================= WS CORE =================
  const toWebSocketUrl = (url) => {
    if (/^wss?:\/\//i.test(url)) return url;
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    return `${proto}://${window.location.host}${
      url.startsWith("/") ? url : `/${url}`
    }`;
  };

  const connect = (url) => {
    if (isStale) return;

    const wsUrl = toWebSocketUrl(url);
    socket = new WebSocket(wsUrl);

    socket.onopen = () => {
      console.log("WebSocket connected");
      store.dispatch(setWebSocketStatus("connected"));
      reconnectAttempts = 0;
      heartbeatStarted = false;
    };

    socket.onmessage = (event) => {
      const raw =
        typeof event.data === "string" ? event.data.trim() : event.data;

      if (raw === "pong" || raw === '"pong"') {
        //console.log("[WS] <- pong");
        return;
      }

      try {
        const parsed = JSON.parse(raw);

        // ================= NEW: SERVER FORCE DISCONNECT (fatal, no reconnect) =================
        // BE salje: {"Disconnect":"Login failed"} (poruka ide na UI), a socket se zatvara bez reconnect loop-a.
        if (parsed?.Disconnect) {
          isStale = true; // bitno: onclose nece raditi reconnect kad je isStale = true
          stopHeartbeat();
          stopBalancePolling();
          unwatchPending();
          
          // prikazi poruku na front-u (reuse postojece logike)
          plinkoTransport.playerException(parsed.Disconnect);
          store.dispatch(setServerMessage(parsed.Disconnect));

          store.dispatch(setWebSocketStatus("disconnected"));

          try {
            socket?.close(4001, "server_disconnect");
          } catch {}

          return;
        }
        // ================= END NEW =================

        if (parsed?.session_status === "STALE") {
          isStale = true;
          stopHeartbeat();
          stopBalancePolling();
          unwatchPending();
          store.dispatch(setWebSocketStatus("stale"));
          socket.close();
          return;
        }

        if (parsed?.login) {
          plinkoTransport.login(parsed.login);
          store.dispatch(setLoginData(parsed.login));

          // heartbeat STARTUJE SAMO JEDNOM po konekciji
          startHeartbeat();

          if (parsed.login.sid) {
            startBalancePolling(parsed.login.sid);
          }
          return;
        }

        if (parsed?.get_user_balance) {
          plinkoTransport.balance(parsed.get_user_balance.balance);
          return;
        }

        if (parsed?.balance !== undefined) {
          plinkoTransport.balance(
            typeof parsed.balance === "number"
              ? parsed.balance
              : parsed.balance?.balance
          );
          return;
        }

        if (parsed?.bet_ack) return plinkoTransport.betAck(parsed.bet_ack);
        if (parsed?.bet_history)
          return plinkoTransport.betHistory(parsed.bet_history);
        if (parsed?.cashout_ack)
          return plinkoTransport.cashoutAck(parsed.cashout_ack);
        if (parsed?.turbo_bet_ack)
          return plinkoTransport.turboBetAck(parsed.turbo_bet_ack);
        if (parsed?.turbo_item)
          return plinkoTransport.turboBetItem(parsed.turbo_item);
        if (parsed?.turbo_cashout)
          return plinkoTransport.turboCashout(parsed.turbo_cashout);
        if (parsed?.Player_exception)
          return plinkoTransport.playerException(parsed.Player_exception);
        if (parsed?.jackpot_data)
          return plinkoTransport.jackpot(parsed.jackpot_data);
        if (parsed?.bonus_expired)
          return plinkoTransport.bonusExpired(parsed.bonus_expired);
        if (parsed?.message?.MessageTypeID === 24)
          return plinkoTransport.freeTickets(parsed);
      } catch (e) {
        console.error("WS parse error:", e, raw);
      }
    };

    socket.onclose = (e) => {
      console.log("[WS] closed", e.code, e.reason);
      store.dispatch(setWebSocketStatus(isStale ? "stale" : "disconnected"));

      stopHeartbeat();
      stopBalancePolling();
      unwatchPending();

      if (!isStale && reconnectAttempts < 50) {
        reconnectAttempts++;
        setTimeout(
          () => connect(wsUrl),
          Math.min(10_000, reconnectAttempts * 1000)
        );
      }
    };

    socket.onerror = () => {
      store.dispatch(setWebSocketStatus("error"));
    };
  };

  // ================= REDUX =================
  return (next) => (action) => {
    if (action.type === "WEBSOCKET_CONNECT") {
      connect(action.payload.url);
    }

    if (action.type === "WEBSOCKET_SEND") {
      send(action.payload);
    }

    if (action.type === "WEBSOCKET_DISCONNECT") {
      try {
        socket?.close();
      } catch {}
    }

    return next(action);
  };
};

export default websocketMiddleware;
